// src/pages/AdminDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import { subscribeTasks, subscribeShipments, subscribeUsers, subscribeActivity } from '../firebase/firestore';

const STATUS_COLOR = {
  Pending: 'var(--gray-500)', 'In Transit': 'var(--blue)', Arrived: '#639922',
  'Customs Clearance': 'var(--amber)', Cleared: 'var(--teal)',
  Delivered: 'var(--green)', 'On Hold': 'var(--red)',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tasks,     setTasks]     = useState([]);
  const [shipments, setShipments] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [activity,  setActivity]  = useState([]);

  useEffect(() => {
    const u1 = subscribeTasks(setTasks);
    const u2 = subscribeShipments(setShipments);
    const u3 = subscribeUsers(setUsers);
    const u4 = subscribeActivity(setActivity);
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  const total   = tasks.length;
  const open    = tasks.filter(t => t.status !== 'done').length;
  const overdue = tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'done').length;
  const done    = tasks.filter(t => t.status === 'done').length;

  const shipCounts = {};
  shipments.forEach(s => { shipCounts[s.status] = (shipCounts[s.status] || 0) + 1; });
  const maxShipCount = Math.max(...Object.values(shipCounts), 1);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Admin dashboard</div>
            <div className="page-sub">{new Date().toLocaleDateString('en-GB',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}</div>
          </div>
          <div className="btn-group">
            <button className="btn primary" onClick={() => navigate('/admin/users')}><i className="ti ti-user-plus"></i> Invite user</button>
          </div>
        </div>
        <div className="scroll">
          <div className="metrics-grid">
            <div className="metric blue"><div className="metric-num">{total}</div><div className="metric-label">Total tasks</div></div>
            <div className="metric amber"><div className="metric-num">{open}</div><div className="metric-label">Open tasks</div></div>
            <div className="metric red"><div className="metric-num">{overdue}</div><div className="metric-label">Overdue</div></div>
            <div className="metric green"><div className="metric-num">{done}</div><div className="metric-label">Completed</div></div>
            <div className="metric blue"><div className="metric-num">{users.length}</div><div className="metric-label">Team members</div></div>
            <div className="metric purple"><div className="metric-num">{shipments.length}</div><div className="metric-label">Shipments</div></div>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-checklist" style={{color:'var(--blue)'}}></i> Recent tasks</span></div>
              {tasks.slice(0,5).map(t => (
                <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid var(--gray-200)'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600}}>{t.title}</div>
                    <div style={{fontSize:11,color:'var(--gray-500)'}}>{t.assigneeName || 'Unassigned'}</div>
                  </div>
                  <span className={`badge ${t.status === 'done' ? 'done' : t.status === 'urgent' ? 'urgent' : 'progress'}`}>{t.status}</span>
                </div>
              ))}
              {tasks.length === 0 && <div className="empty-state"><i className="ti ti-checklist"></i><p>No tasks yet</p></div>}
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-users" style={{color:'var(--blue)'}}></i> Team members</span></div>
              {users.map(u => (
                <div key={u.id} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid var(--gray-200)'}}>
                  <div className="avatar" style={{background:'var(--blue)',fontSize:10}}>
                    {u.name?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600}}>{u.name}</div>
                    <div style={{fontSize:11,color:'var(--gray-500)'}}>{u.email}</div>
                  </div>
                  <span className={`badge ${u.role}`}>{u.role}</span>
                </div>
              ))}
              {users.length === 0 && <div className="empty-state"><i className="ti ti-users"></i><p>No users yet</p></div>}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title"><i className="ti ti-chart-donut" style={{color:'var(--purple)'}}></i> Status breakdown</span></div>
            {Object.keys(shipCounts).length === 0
              ? <div className="empty-state"><i className="ti ti-chart-donut"></i><p>No shipments yet</p></div>
              : Object.entries(shipCounts).map(([st, n]) => (
                <div key={st} className="bar-row">
                  <div className="bar-meta"><span>{st}</span><span style={{fontWeight:600}}>{n}</span></div>
                  <div className="bar-bg">
                    <div className="bar-fill" style={{width:`${Math.round(n/maxShipCount*100)}%`, background: STATUS_COLOR[st] || 'var(--gray-500)'}} />
                  </div>
                </div>
              ))}
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title"><i className="ti ti-activity" style={{color:'var(--blue)'}}></i> Activity log</span></div>
            {activity.slice(0,8).map(a => (
              <div key={a.id} style={{display:'flex',gap:8,padding:'7px 0',borderBottom:'1px solid var(--gray-200)',fontSize:12}}>
                <div className="avatar" style={{background:'var(--blue)',fontSize:9,width:24,height:24}}>
                  {a.userName?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()}
                </div>
                <div style={{flex:1,color:'var(--gray-600)'}}>{a.message}</div>
                <div style={{fontSize:11,color:'var(--gray-500)',whiteSpace:'nowrap'}}>
                  {a.timestamp?.toDate ? a.timestamp.toDate().toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : a._time || '—'}
                </div>
              </div>
            ))}
            {activity.length === 0 && <div className="empty-state"><i className="ti ti-activity"></i><p>No activity yet</p></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
